import { useRef } from 'react';
import { FaUserPlus, FaWallet, FaBolt, FaChartBar, FaGift, FaShieldAlt } from 'react-icons/fa';
import useIntersectionObserver from '../hooks/useIntersectionObserver';
import './Work.css';

const howItWorksSteps = [
  { icon: <FaUserPlus size={28} />, title: 'Sign Up', desc: 'Create your RSKPay account in seconds.', delay: '0.1s' },
  { icon: <FaWallet size={28} />, title: 'Add Money', desc: 'Load your wallet or link your UPI.', delay: '0.2s' },
  { icon: <FaBolt size={28} />, title: 'Recharge & Pay', desc: 'Mobile, bills, DTH, loans – all covered.', delay: '0.3s' },
  { icon: <FaChartBar size={28} />, title: 'Track & Manage', desc: 'Get detailed history and insights.', delay: '0.4s' },
  { icon: <FaGift size={28} />, title: 'Earn Rewards', desc: 'Get cashback and rewards on every transaction.', delay: '0.5s' },
  { icon: <FaShieldAlt size={28} />, title: 'Stay Safe', desc: 'Secure transactions with advanced encryption.', delay: '0.6s' },
];

function Work() {
  const { sectionRef, isVisible } = useIntersectionObserver();

  return (
    <section id="how-it-works" className="work-section" ref={sectionRef}>
      <div className="work-container">
        
        <h2 className={`work-title ${isVisible ? 'animate' : ''}`}>How It Works</h2>
        
        {/* SVG Graphic */}
        <div className="work-graphic">
          <svg width="600" height="80" viewBox="0 0 600 80" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" style={{ stopColor: 'rgba(255, 255, 255, 0.3)', stopOpacity: 1 }} />
                <stop offset="50%" style={{ stopColor: 'rgba(255, 255, 255, 0.6)', stopOpacity: 1 }} />
                <stop offset="100%" style={{ stopColor: 'rgba(255, 255, 255, 0.3)', stopOpacity: 1 }} />
              </linearGradient>
            </defs>
            
            {/* Horizontal flow path */}
            <line 
              x1="30" 
              y1="40" 
              x2="570" 
              y2="40" 
              stroke="url(#lineGradient)" 
              strokeWidth="3" 
              className="flow-path"
            />
            
            {/* Step nodes - horizontally arranged (6 nodes) */}
            <circle cx="30" cy="40" r="20" fill="rgba(255, 255, 255, 0.2)" stroke="rgba(255, 255, 255, 0.6)" strokeWidth="2" className="step-node" style={{ animationDelay: '0s' }}/>
            <circle cx="138" cy="40" r="20" fill="rgba(255, 255, 255, 0.2)" stroke="rgba(255, 255, 255, 0.6)" strokeWidth="2" className="step-node" style={{ animationDelay: '0.2s' }}/>
            <circle cx="246" cy="40" r="20" fill="rgba(255, 255, 255, 0.2)" stroke="rgba(255, 255, 255, 0.6)" strokeWidth="2" className="step-node" style={{ animationDelay: '0.4s' }}/>
            <circle cx="354" cy="40" r="20" fill="rgba(255, 255, 255, 0.2)" stroke="rgba(255, 255, 255, 0.6)" strokeWidth="2" className="step-node" style={{ animationDelay: '0.6s' }}/>
            <circle cx="462" cy="40" r="20" fill="rgba(255, 255, 255, 0.2)" stroke="rgba(255, 255, 255, 0.6)" strokeWidth="2" className="step-node" style={{ animationDelay: '0.8s' }}/>
            <circle cx="570" cy="40" r="20" fill="rgba(255, 255, 255, 0.2)" stroke="rgba(255, 255, 255, 0.6)" strokeWidth="2" className="step-node" style={{ animationDelay: '1s' }}/>
            
            {/* Number labels */}
            <text x="30" y="45" textAnchor="middle" fill="#ffffff" fontSize="14" fontWeight="bold">1</text>
            <text x="138" y="45" textAnchor="middle" fill="#ffffff" fontSize="14" fontWeight="bold">2</text>
            <text x="246" y="45" textAnchor="middle" fill="#ffffff" fontSize="14" fontWeight="bold">3</text>
            <text x="354" y="45" textAnchor="middle" fill="#ffffff" fontSize="14" fontWeight="bold">4</text>
            <text x="462" y="45" textAnchor="middle" fill="#ffffff" fontSize="14" fontWeight="bold">5</text>
            <text x="570" y="45" textAnchor="middle" fill="#ffffff" fontSize="14" fontWeight="bold">6</text>
            
            {/* Animated particles - high density continuous dots */}
            <circle cx="30" cy="40" r="4" fill="rgba(255, 255, 255, 0.95)">
              <animate attributeName="cx" from="30" to="570" dur="3s" repeatCount="indefinite"/>
            </circle>
            <circle cx="30" cy="40" r="4" fill="rgba(255, 255, 255, 0.9)">
              <animate attributeName="cx" from="30" to="570" dur="3s" begin="0.3s" repeatCount="indefinite"/>
            </circle>
            <circle cx="30" cy="40" r="4" fill="rgba(255, 255, 255, 0.85)">
              <animate attributeName="cx" from="30" to="570" dur="3s" begin="0.6s" repeatCount="indefinite"/>
            </circle>
            <circle cx="30" cy="40" r="4" fill="rgba(255, 255, 255, 0.8)">
              <animate attributeName="cx" from="30" to="570" dur="3s" begin="0.9s" repeatCount="indefinite"/>
            </circle>
            <circle cx="30" cy="40" r="4" fill="rgba(255, 255, 255, 0.75)">
              <animate attributeName="cx" from="30" to="570" dur="3s" begin="1.2s" repeatCount="indefinite"/>
            </circle>
            <circle cx="30" cy="40" r="4" fill="rgba(255, 255, 255, 0.7)">
              <animate attributeName="cx" from="30" to="570" dur="3s" begin="1.5s" repeatCount="indefinite"/>
            </circle>
            <circle cx="30" cy="40" r="4" fill="rgba(255, 255, 255, 0.65)">
              <animate attributeName="cx" from="30" to="570" dur="3s" begin="1.8s" repeatCount="indefinite"/>
            </circle>
            <circle cx="30" cy="40" r="4" fill="rgba(255, 255, 255, 0.6)">
              <animate attributeName="cx" from="30" to="570" dur="3s" begin="2.1s" repeatCount="indefinite"/>
            </circle>
            <circle cx="30" cy="40" r="4" fill="rgba(255, 255, 255, 0.55)">
              <animate attributeName="cx" from="30" to="570" dur="3s" begin="2.4s" repeatCount="indefinite"/>
            </circle>
            <circle cx="30" cy="40" r="4" fill="rgba(255, 255, 255, 0.5)">
              <animate attributeName="cx" from="30" to="570" dur="3s" begin="2.7s" repeatCount="indefinite"/>
            </circle>
          </svg>
        </div>

        <div className="work-grid">
          {howItWorksSteps.map((step, idx) => (
            <div 
              key={idx} 
              className={`work-card ${isVisible ? 'fade-in' : ''}`} 
              style={{ animationDelay: step.delay }}
            >
              <div className="step-number">
                {idx + 1}
              </div>
              <div className="step-icon">
                {step.icon}
              </div>
              <h3>{step.title}</h3>
              <p>{step.desc}</p>
            </div>
          ))}
        </div>

      </div>
    </section>
  );
}

export default Work;
